import * as GETTERS from '@/store/constants/getters.js';
import * as MUTATIONS from '@/store/constants/mutations.js';
import * as ACTIONS from '@/store/constants/actions.js';
import * as ROUTES from '@/constants/routes.js';
import {
    ATTENTION_TIME_API_ACTION_MAP,
    TIME_EVENTS,
    TIME_TRACKING_EVENTS,
    ATTENTION_TIME_FIELDS,
} from '@/constants/time.js';
import { sendAnalyticsBeacon } from '@/resources/rest_api_v4.js';
import { logInfo } from '@/util/logger.js';

const ACTIVITY_DELAY = 250;
const ACTIVE_TIME_THRESHOLD = 1000 * 60 * 10;
const MINIMUM_ATTENTION_TIME = 1000;

export const actions = {
    [ACTIONS.UPDATE_LAST_ACTIVE]({ commit, getters }) {
        const currentTime = getters[GETTERS.CURRENT_TIME];
        const timeElapsed = currentTime - getters[GETTERS.LAST_ACTIVE];

        // throttle the update a little
        if (timeElapsed > ACTIVITY_DELAY) {
            commit(MUTATIONS.UPDATE_LAST_ACTIVE);
            const currentQueueUuid = getters[GETTERS.CURRENT_QUEUE_UUID];

            // If it is a search OR the batch completed screen, then set the user as active
            // For queues, we only set the user as active when they click the "I'm still here" button
            if (currentQueueUuid) {
                const queue = getters[GETTERS.QUEUE_BY_UUID](currentQueueUuid);
                const batchIsEmpty = !queue || queue.content.length === 0;

                if (batchIsEmpty) {
                    commit(MUTATIONS.SET_INACTIVE, false);
                }
            }
            else {
                commit(MUTATIONS.SET_INACTIVE, false);
            }
        }
    },

    [ACTIONS.INCREMENT_ATTENTION_TIME]({ commit, getters }, time) {
        // The user is inactive, don't do any analytics tracking
        if (getters[GETTERS.IS_INACTIVE]) {
            return;
        }
        const timeField = getters[GETTERS.GET_CURRENT_TIME_FIELD];

        if (timeField) {
            commit(MUTATIONS.INCREMENT_TIME, {
                key: timeField,
                time,
            });

            if (getters[GETTERS.GET_EVENT_HISTORY].length === 0) {
                commit(MUTATIONS.ADD_EVENT_TO_HISTORY, TIME_TRACKING_EVENTS.start());
            }
        }
    },

    [ACTIONS.SEND_ATTENTION_TIME]({ commit, getters }, { field = getters[GETTERS.GET_CURRENT_TIME_FIELD], action_source = TIME_EVENTS.ROUTE_CHANGE }) {
        const workspace_uuid = getters[GETTERS.CURRENT_WORKSPACE_UUID];
        const queue_uuid = getters[GETTERS.CURRENT_QUEUE_UUID];
        const saved_search_uuid = getters[GETTERS.CURRENT_SEARCH_UUID];
        const site_uuid = getters[GETTERS.ACTIVE_SITE_UUID];

        const time_spent = getters[GETTERS.GET_TIME](field);
        // Send the attention time if there is at least 1s. We are getting weird cases where very small values are being reported, so lets just have a minimum
        // Anything less than a second is not worth tracking anyways
        if (time_spent >= MINIMUM_ATTENTION_TIME) {
            // If the user has been on the queue page for a suspiciously long time, lets log it and see whats happening
            if (time_spent > ACTIVE_TIME_THRESHOLD && field === ATTENTION_TIME_FIELDS[ROUTES.QUEUE]) {
                logInfo(new Error('User on page for over 10 minutes'),
                    'attention_time_on_page_over_threshold',
                    [ {
                        key: 'time',
                        value: time_spent,
                    },
                    // NOTE this is an experiment to see if we can see why people are spending so long on queues
                    // Keep track of various timing events to see if we can learn more about what they're doing, or if we have a bug
                    {
                        key: 'event_history',
                        value: getters[GETTERS.GET_EVENT_HISTORY],
                    } ]);
            }

            commit(MUTATIONS.CLEAR_EVENT_HISTORY);

            sendAnalyticsBeacon({
                action: ATTENTION_TIME_API_ACTION_MAP[field],
                workspace_uuid,
                queue_uuid,
                site_uuid,
                saved_search_uuid,
                time_spent,
                action_source,
                console_version: getters[GETTERS.CONSOLE_VERSION],
            });
            commit(MUTATIONS.RESET_TIME, field);
        }
    },
};
